import { useState } from 'react';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import {
  projectsApi, usersApi, manpowerApi,
  type ManpowerActuals, type EstimatedHours, type LeadersNeeded,
} from '@/lib/apiClient';
import { fmtDate } from '@/lib/dates';
import { jobActualStart, jobActualStartUnclaimed, type ProjectPhase } from '@/lib/schedule';
import { weekLabel, weekBarLabel } from './lib/weekLabel';
import ComingSoon from '@/components/ComingSoon';
import ManpowerRequestCard from './ManpowerRequestCard';
import TravelAcknowledgmentsCard from './TravelAcknowledgmentsCard';

// Actual daily labor pulled from Paycor hours (synced into safety-site by the
// safety tool) — read-only mirror, same as Cost/Safety. v1 = summary tiles +
// daily crew/hours table with a Reg/OT bar; roster + cost-code breakdown later.
//
// Projected hours (top card) are ENTERED BY THE ESTIMATOR at job set-up, and the
// job is flagged until they are. They used to come from a weekly Sage Intacct
// "Project Hours Summary" CSV; that sync was removed on 2026-08-04 because the
// figure was autocalculated and routinely lower than the real labor (product
// owner), so the app was showing a number nobody stood behind. What the sync
// last imported survives as a frozen, explicitly-unverified reference — never
// as the value.
const css = `
  .mp-tiles { display: grid; gap: var(--ew-s-4); grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); margin-bottom: var(--ew-s-4); }
  .mp-kpi { background: #fff; border: 1px solid var(--ew-n-100); border-radius: var(--ew-r-lg); padding: 14px 16px; display: flex; flex-direction: column; gap: 5px; }
  .mp-kpi-label { font: var(--ew-fw-semibold) 10px/1.3 var(--ew-font); letter-spacing: .06em; text-transform: uppercase; color: var(--ew-n-500); }
  .mp-kpi-value { font: var(--ew-fw-bold) 22px/1.15 var(--ew-font); color: var(--ew-dark); font-variant-numeric: tabular-nums; }
  .mp-card-hd { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; flex-wrap: wrap; padding: var(--ew-s-4); border-bottom: 1px solid var(--ew-n-100); }
  .mp-title { font: var(--ew-fw-semibold) var(--ew-fs-body) var(--ew-font); color: var(--ew-dark); margin: 0; }
  .mp-sub { font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); color: var(--ew-n-500); }
  .mp-legend-ot { display: inline-block; width: 8px; height: 8px; border-radius: 2px; background: #E0A100; vertical-align: middle; margin: 0 3px 1px 8px; }
  .mp-legend-reg { display: inline-block; width: 8px; height: 8px; border-radius: 2px; background: var(--ew-green); vertical-align: middle; margin: 0 3px 1px 0; }
  .mp-scroll { max-height: 460px; overflow-y: auto; overflow-x: auto; -webkit-overflow-scrolling: touch; }
  .mp-table { width: 100%; border-collapse: collapse; font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); }
  .mp-table th { position: sticky; top: 0; background: #fff; z-index: 1; text-align: left; padding: 9px var(--ew-s-4); white-space: nowrap; font: var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption); text-transform: uppercase; color: var(--ew-n-500); border-bottom: 1px solid var(--ew-n-100); }
  .mp-table td { padding: 9px var(--ew-s-4); border-bottom: 1px solid var(--ew-n-100); color: var(--ew-dark); white-space: nowrap; }
  .mp-table tbody tr:nth-child(even) td { background: #FAFAFA; }
  .mp-table tbody tr:hover td { background: var(--ew-green-50, #F0FDF4); }
  .mp-num { text-align: right; font-variant-numeric: tabular-nums; }
  .mp-table th.mp-num { text-align: right; }
  .mp-muted { color: var(--ew-n-500); }
  .mp-bar-col { width: 100%; min-width: 140px; }
  .mp-bar-track { display: flex; height: 8px; width: 100%; background: var(--ew-n-100); border-radius: 999px; overflow: hidden; }
  .mp-bar-reg { background: var(--ew-green); height: 100%; }
  .mp-bar-ot { background: #E0A100; height: 100%; }

  /* Projected-hours card */
  .eh-card { margin-bottom: var(--ew-s-4); }
  .eh-body { padding: var(--ew-s-4); display: flex; flex-wrap: wrap; gap: 20px 32px; align-items: flex-start; }
  .eh-figure { display: flex; flex-direction: column; gap: 2px; min-width: 150px; }
  .eh-value { font: var(--ew-fw-bold) 30px/1.1 var(--ew-font); color: var(--ew-dark); font-variant-numeric: tabular-nums; }
  .eh-unit { font: var(--ew-fw-semibold) 10px/1.3 var(--ew-font); letter-spacing: .06em; text-transform: uppercase; color: var(--ew-n-500); }
  .eh-meta { flex: 1 1 280px; display: flex; flex-direction: column; gap: 8px; min-width: 240px; }
  .eh-breakdown { font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); color: var(--ew-n-500); }
  .eh-breakdown b { color: var(--ew-dark); font-weight: var(--ew-fw-semibold); }
  .eh-prog-track { height: 8px; width: 100%; background: var(--ew-n-100); border-radius: 999px; overflow: hidden; }
  .eh-prog-fill { height: 100%; }
  .eh-prog-label { font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); color: var(--ew-dark); }
  .eh-actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
  .eh-btn { font: var(--ew-fw-semibold) 12px var(--ew-font); border-radius: var(--ew-r-md); padding: 6px 12px; cursor: pointer; border: 1px solid var(--ew-n-100); background: #fff; color: var(--ew-dark); }
  .eh-btn-primary { background: var(--ew-green, #2d8c54); color: #fff; border-color: transparent; }
  .eh-btn:disabled { opacity: .5; cursor: default; }
  .eh-link { background: none; border: none; padding: 0; cursor: pointer; color: var(--ew-green, #2d8c54); font: var(--ew-fw-semibold) 12px var(--ew-font); text-decoration: underline; }
  .eh-input { width: 130px; font: var(--ew-fw-regular) 14px var(--ew-font); padding: 6px 9px; border: 1px solid var(--ew-n-100); border-radius: var(--ew-r-md); font-variant-numeric: tabular-nums; }
  .eh-err { color: #b91c1c; font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); }
  .eh-badge { font: var(--ew-fw-semibold) 10px var(--ew-font); letter-spacing: .05em; text-transform: uppercase; padding: 2px 7px; border-radius: var(--ew-r-pill, 999px); }

  /* Traveling leaders (#396 slice 2) */
  .tl-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-top: 14px; padding-top: 12px; border-top: 1px solid var(--ew-n-100); }
  .tl-value { font: var(--ew-fw-bold) 20px/1 var(--ew-font); color: var(--ew-dark); font-variant-numeric: tabular-nums; }
  .tl-badge { font: var(--ew-fw-semibold) 10px var(--ew-font); letter-spacing: .05em; text-transform: uppercase; padding: 2px 7px; border-radius: var(--ew-r-pill, 999px); }
  .tl-badge-default { background: var(--ew-n-100); color: var(--ew-n-500); }
  .tl-badge-override { background: var(--ew-quality-100, #e0f2fe); color: var(--ew-quality-700, #0369a1); }
  .tl-input { width: 70px; font: var(--ew-fw-regular) 14px var(--ew-font); padding: 5px 8px; border: 1px solid var(--ew-n-100); border-radius: var(--ew-r-md); font-variant-numeric: tabular-nums; }
`;

const fmtHrs = (n: number) => n.toLocaleString('en-US', { maximumFractionDigits: 1 });

/**
 * Takes only the job. Until #303 slice 2 this also took the workspace's
 * ACTIVITY lock (`locked`), which gated exactly one thing here — the manpower
 * request card — and nothing else on the tab.
 *
 * That gate moved server-side: `GET /manpower/requests/new-context` returns a
 * `lifecycle` and a `canRequest` computed by the same code that enforces the
 * POST, and the card reads those. The workspace's all-or-nothing lock could not
 * express decision 2 (product owner, 2026-08-31) — a ClosedWon job MAY take a request, a
 * SYSTAC bid may not — so passing it down would have meant either hiding the
 * card on won jobs or re-deriving the rule in a second place.
 *
 * Nothing else on this tab was ever gated by the lock. Projected hours are the
 * estimator's to enter at job set-up, and whether THAT should wait for a Sage
 * number is a separate decision nobody has made.
 */
export default function ManpowerTab({ projectId }: { projectId: number }) {
  const q = useQuery({
    queryKey: ['manpower', projectId],
    queryFn: () => projectsApi.manpower(projectId) as Promise<ManpowerActuals>,
    staleTime: 5 * 60 * 1000,
  });

  const actualHours = q.data?.available ? q.data.summary?.totalHours ?? null : null;

  /**
   * When work actually began on this job (#482).
   *
   * ⚠️ This used to live on the Overview's Schedule & Dates card. Asked directly,
   * the PM who raised #460 and #482 did not want it there or on the projects
   * list — it is a field-hours fact, so it belongs beside the field hours.
   *
   * ⚠️ Reuses the SAME query key the workspace loads the project under, so it is
   * served from cache rather than costing a second request.
   *
   * The precedence mirrors the Overview's seeding, and has to:
   *   • a recorded actual on any phase wins — earliest, because a fab job records
   *     one per phase and the job began when the first of them did;
   *   • otherwise the Paycor-derived date, but ONLY where nothing has been spoken
   *     for ON ANY PHASE — a fab job has no 'project' row to ask. A PM who
   *     cleared the start said "delayed indefinitely", and showing
   *     a derived date over that is #394 one screen along.
   *
   * Job-level Paycor is the right source HERE even on a fab job. #380's
   * restriction is that MIN(work_date) cannot say which PHASE began; this tile
   * asks about the job, which is the question that date does answer.
   */
  const projectQ = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.get(projectId) as Promise<{ phases?: ProjectPhase[] }>,
    staleTime: 5 * 60 * 1000,
  });
  const phases = projectQ.data?.phases ?? [];
  const actualStart = jobActualStart(phases)
    ?? (jobActualStartUnclaimed(phases)
      ? (q.data?.available ? q.data.derivedActualStart : null)
      : null);

  return (
    <>
      <style>{css}</style>
      {/* Plan first, then actuals. Until now this tab only ever showed hours
          already worked, so a PM had nowhere to see how their own job was
          scheduled — the rollup is a company-wide editor's grid, not an answer
          to "how is my job manned" (product owner, 2026-07-28). */}
      <ScheduledCrewCard projectId={projectId} />
      {/* Directly under the scheduled crew, because that card is where a PM
          realises the crew is wrong — the request belongs at the point the
          problem is seen, not at the bottom of the tab (issue #303, slice A). */}
      {/* The card decides its own visibility from GET /manpower/requests/new-context
          (lifecycle + canRequest, one server-side rule). It no longer takes the
          workspace's `locked`: that was all-or-nothing, and decision 2 (2026-08-31)
          splits it — a ClosedWon job may take a request, a SYSTAC bid may not. */}
      <ManpowerRequestCard projectId={projectId} />
      {/* Travel Acknowledgments. Renders nothing without `manpower.edit`,
          which the product owner ruled is the only role responsible for them
          (2026-09-22) — a PM on this tab sees no change.

          Shown on every job rather than gated on a travel band: the tab would
          need a second fetch to know the band, and a job that cannot state an
          entitlement already refuses with a message naming who can fix it,
          which is more useful than a card that silently is not there.

          ⚠️ `key={projectId}` IS LOAD-BEARING. `/projects/:id` is one
          <Route> element, so moving from one job to another re-renders this
          same card instance and keeps its state — the caveats, the error and
          the picker selection from the PREVIOUS job, shown on this one. An
          exempt-status warning about somebody on another job is exactly the
          misattribution the card's `caveatsFor` exists to prevent. Keying on
          the job remounts the card, so nothing it holds can cross between
          jobs. (Codex, P2 on #542.) */}
      <TravelAcknowledgmentsCard key={projectId} projectId={projectId} />
      <PlanVsActualCard projectId={projectId} />
      <ProjectedHoursCard projectId={projectId} actualHours={actualHours} />
      <PaycorSection q={q} actualStart={actualStart} />
    </>
  );
}

// ─── Scheduled crew (published crew-schedule mirror) ─────────────────────────

function ScheduledCrewCard({ projectId }: { projectId: number }) {
  const q = useQuery({
    queryKey: ['manpower', 'schedule-summary', projectId],
    queryFn: () => manpowerApi.projectScheduleSummary(projectId, 8),
    staleTime: 60_000,
  });

  // A job with no crew on the grid gets nothing at all — a row of zeros would
  // read as "scheduled for nobody" rather than "not scheduled".
  if (!q.data || q.data.scheduled === false) return null;
  const { ahead, weeks, isFab, leadersNeeded } = q.data;

  // Scale the bars against the busiest week on screen, not an absolute ceiling,
  // so a 3-crew job and a 30-crew job are both readable.
  const maxPeak = Math.max(1, ...weeks.map((w) => w.peak));

  return (
    <div className="pw-card" style={{ marginBottom: 'var(--ew-s-4)' }}>
      <div className="mp-card-hd">
        <div>
          <h3 className="mp-title">Scheduled Crew</h3>
          <div className="mp-sub">
            Published crew schedule · weekly figures are the week’s peak day
          </div>
        </div>
        <span style={{ font: 'var(--ew-fw-semibold) 10px/1 var(--ew-font)', letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--ew-quality)' }}>
          ◇ From the Manpower schedule
        </span>
      </div>

      <div style={{ padding: 'var(--ew-s-4)' }}>
        <div className="mp-tiles" style={{ marginBottom: 'var(--ew-s-4)' }}>
          <div className="mp-kpi">
            <span className="mp-kpi-label">Crew-days ahead</span>
            <span className="mp-kpi-value">{ahead.crewDays.toLocaleString()}</span>
            <span className="mp-sub">over {ahead.days} scheduled day{ahead.days === 1 ? '' : 's'}</span>
          </div>
          <div className="mp-kpi">
            <span className="mp-kpi-label">Peak crew/day</span>
            <span className="mp-kpi-value">{ahead.peakCrew}</span>
            <span className="mp-sub">busiest day still to come</span>
          </div>
          <div className="mp-kpi">
            <span className="mp-kpi-label">Next scheduled</span>
            <span className="mp-kpi-value" style={{ fontSize: 17 }}>{ahead.firstDay ? fmtDate(ahead.firstDay) : '—'}</span>
            <span className="mp-sub">{ahead.firstDay ? '' : 'nothing scheduled ahead'}</span>
          </div>
          <div className="mp-kpi">
            <span className="mp-kpi-label">Last scheduled</span>
            <span className="mp-kpi-value" style={{ fontSize: 17 }}>{ahead.lastDay ? fmtDate(ahead.lastDay) : '—'}</span>
            <span className="mp-sub">end of the current plan</span>
          </div>
        </div>

        <div style={{ font: 'var(--ew-fw-semibold) 11px/1 var(--ew-font)', letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ew-n-500)', marginBottom: 8 }}>
          Next 8 weeks
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', overflowX: 'auto', paddingBottom: 4 }}>
          {weeks.map((w) => (
            <div key={w.weekStart} style={{ flex: '1 0 62px', minWidth: 62, textAlign: 'center' }}
                 title={isFab
                   ? `Week of ${weekLabel(w.weekStart)} — peak ${w.peak} (fab ${w.fabPeak}, install ${w.instPeak}) · ${w.crewDays} crew-days`
                   : `Week of ${weekLabel(w.weekStart)} — peak ${w.peak} · ${w.crewDays} crew-days`}>
              <div style={{ height: 64, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
                <div style={{
                  width: '100%',
                  height: `${Math.round((w.peak / maxPeak) * 100)}%`,
                  minHeight: w.peak > 0 ? 3 : 0,
                  background: w.peak > 0 ? 'var(--ew-green)' : 'transparent',
                  borderRadius: '3px 3px 0 0',
                }} />
              </div>
              <div style={{ font: 'var(--ew-fw-semibold) 12px/1.4 var(--ew-font)', color: w.peak > 0 ? 'var(--ew-dark)' : 'var(--ew-n-400)', fontVariantNumeric: 'tabular-nums' }}>
                {w.peak || '—'}
              </div>
              {/* "wk Sep 20", not "Sep 20" — a bare date under a crew count
                  reads as the day the crew starts, which is what #452 was. */}
              <div style={{ fontSize: 10.5, color: 'var(--ew-n-500)' }}>{weekBarLabel(w.weekStart)}</div>
            </div>
          ))}
        </div>

        {isFab && (
          <div style={{ marginTop: 10, fontSize: 11.5, color: 'var(--ew-n-500)' }}>
            Fabrication job — hover a week for the fabrication / installation split.
          </div>
        )}

        {leadersNeeded && <TravelingLeadersRow projectId={projectId} data={leadersNeeded} />}
      </div>
    </div>
  );
}

/**
 * Leaders needed (#396 slice 2), on EVERY scheduled job.
 *
 * ⚠️ It was travel-only, and the scope came off when the Gap Analysis tab grew
 * a Local leadership chart and a Local jobs table. Those read the same figure
 * for a local crew, so a local job's number has to be correctable from its own
 * tab — otherwise it is a figure somebody can see and nobody can change.
 *
 * Only the LABEL depends on the job type: the rule (1 under 7 peak crew, 2 at
 * 7 or more) and the override are identical on both. "Traveling leaders" on a
 * travel job is the manpower lead's own wording and worth keeping where it is true.
 *
 * Default vs override are visually distinct (a neutral pill for the default,
 * an accented one for the override) so a PM can tell at a glance whether
 * the number is the rule or a judgment call.
 */
function TravelingLeadersRow({ projectId, data }: { projectId: number; data: LeadersNeeded }) {
  const qc = useQueryClient();
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => usersApi.me(), staleTime: 5 * 60 * 1000 });
  const canEdit = (me?.roles?.includes('Admin') ?? false) || (me?.permissions?.includes('manpower.edit') ?? false);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const save = useMutation({
    mutationFn: (value: number | null) => manpowerApi.setLeadersNeeded(projectId, value),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['manpower', 'schedule-summary', projectId] });
      setEditing(false);
    },
  });

  function beginEdit() {
    setDraft(data.isOverride ? String(data.value) : '');
    save.reset();
    setEditing(true);
  }
  const parsed = draft.trim() === '' ? null : Number(draft);
  const invalid = parsed != null && (!Number.isInteger(parsed) || parsed < 0);

  return (
    <div className="tl-row">
      <span className="mp-kpi-label">{data.travelJob ? 'Traveling leaders needed' : 'Leaders needed'}</span>
      {editing ? (
        <>
          <input
            className="tl-input"
            type="number"
            min={0}
            step={1}
            autoFocus
            value={draft}
            placeholder={String(data.default)}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !invalid) save.mutate(parsed);
              if (e.key === 'Escape') setEditing(false);
            }}
          />
          <button className="eh-btn eh-btn-primary" disabled={invalid || save.isPending} onClick={() => save.mutate(parsed)}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
          <button className="eh-btn" disabled={save.isPending} onClick={() => setEditing(false)}>Cancel</button>
          <span className="mp-sub">Clear the box and save to go back to the default ({data.default}).</span>
          {save.isError && <span className="eh-err">Couldn't save — try again.</span>}
        </>
      ) : (
        <>
          <span className="tl-value">{data.value}</span>
          <span className={`tl-badge ${data.isOverride ? 'tl-badge-override' : 'tl-badge-default'}`}
                title={data.isOverride
                  ? `Overridden. The default from peak crew would be ${data.default}.`
                  : 'Default: 1 under 7 peak crew, 2 at 7 or more.'}>
            {data.isOverride ? 'Override' : 'Default'}
          </span>
          {/* canOverride is the server's answer, not ours: it is false on a
              database where the leaders_needed migration has not run, and the
              PATCH would fail there. The figure beside it is still right — it
              is the computed default — so the number stays and only the
              control goes. */}
          {canEdit && data.canOverride && (
            <button className="eh-btn" onClick={beginEdit}>{data.isOverride ? 'Edit' : 'Override'}</button>
          )}
        </>
      )}
    </div>
  );
}

// ─── Planned vs actual (schedule ↔ Paycor, weekly) ───────────────────────────

/**
 * Did the plan happen? Scheduled crew-days against people who actually booked
 * field hours to the job, week by week.
 *
 * Unit is CREW-DAYS on both sides — one person on the job for one day. The
 * schedule is a head count, not hours, so comparing hours would need a
 * conversion nobody agreed on; distinct people is a like-for-like match
 * (product owner, 2026-07-28).
 *
 * Weeks past the payroll cutoff are shown as "—", never as a shortfall: the
 * schedule runs to Friday while hours only run to the last sync, and reporting
 * that gap as under-manning would cry wolf every single week.
 */
function PlanVsActualCard({ projectId }: { projectId: number }) {
  const q = useQuery({
    queryKey: ['manpower', 'plan-vs-actual', projectId],
    queryFn: () => manpowerApi.projectPlanVsActual(projectId, 8),
    staleTime: 60_000,
  });

  if (!q.data || q.data.available === false) return null;
  const { weeks, totals, dataThrough } = q.data;
  // Nothing planned and nothing worked in the window — no story to tell.
  if (!weeks.some((w) => w.planned > 0 || w.actual > 0)) return null;

  const varColour = (v: number) => (v < 0 ? '#B5611A' : v > 0 ? 'var(--ew-green-700)' : 'var(--ew-n-500)');
  const varText = (v: number) => (v === 0 ? '—' : `${v > 0 ? '+' : ''}${v}`);

  return (
    <div className="pw-card" style={{ marginBottom: 'var(--ew-s-4)' }}>
      <div className="mp-card-hd">
        <div>
          <h3 className="mp-title">Planned vs Actual Crew</h3>
          <div className="mp-sub">
            Crew-days — scheduled head count against people who booked field hours
          </div>
        </div>
        {dataThrough && (
          <span className="mp-sub">Payroll through {fmtDate(dataThrough)}</span>
        )}
      </div>

      <div style={{ padding: 'var(--ew-s-4)' }}>
        <div className="mp-tiles" style={{ marginBottom: 'var(--ew-s-4)' }}>
          <div className="mp-kpi">
            <span className="mp-kpi-label">Planned</span>
            <span className="mp-kpi-value">{totals.planned.toLocaleString()}</span>
            <span className="mp-sub">crew-days scheduled</span>
          </div>
          <div className="mp-kpi">
            <span className="mp-kpi-label">Actual</span>
            <span className="mp-kpi-value">{totals.actual.toLocaleString()}</span>
            <span className="mp-sub">crew-days worked</span>
          </div>
          <div className="mp-kpi">
            <span className="mp-kpi-label">Variance</span>
            <span className="mp-kpi-value" style={{ color: varColour(totals.variance) }}>
              {varText(totals.variance)}
            </span>
            <span className="mp-sub">
              {totals.variance < 0 ? 'fewer than planned' : totals.variance > 0 ? 'more than planned' : 'on plan'}
            </span>
          </div>
        </div>

        <div className="mp-scroll">
          <table className="mp-table">
            <thead>
              <tr>
                <th>Week of</th>
                <th className="mp-num">Planned</th>
                <th className="mp-num">Actual</th>
                <th className="mp-num">Variance</th>
              </tr>
            </thead>
            <tbody>
              {weeks.map((w) => (
                <tr key={w.weekStart}>
                  <td>
                    {fmtDate(w.weekStart)}
                    {w.partial && <span className="mp-muted"> · part week</span>}
                  </td>
                  <td className="mp-num">{w.comparable ? w.planned : '—'}</td>
                  <td className="mp-num">{w.comparable ? w.actual : '—'}</td>
                  <td className="mp-num" style={{ color: w.comparable ? varColour(w.variance) : undefined, fontWeight: 600 }}>
                    {w.comparable ? varText(w.variance) : <span className="mp-muted">no payroll yet</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mp-sub" style={{ marginTop: 10 }}>
          A crew-day is one person on the job for one day. Both columns stop at the payroll
          cutoff, so a week still being worked isn’t counted as short.
        </div>
      </div>
    </div>
  );
}

// ─── Estimated labor hours (estimator-entered; flagged when missing) ─────────

function ProjectedHoursCard({ projectId, actualHours }: { projectId: number; actualHours: number | null }) {
  const qc = useQueryClient();
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => usersApi.me(), staleTime: 5 * 60 * 1000 });
  const canEdit = (me?.roles?.includes('Admin') ?? false) || (me?.permissions?.includes('projects.edit') ?? false);

  const eh = useQuery({
    queryKey: ['estimated-hours', projectId],
    queryFn: () => projectsApi.estimatedHours(projectId),
    staleTime: 5 * 60 * 1000,
  });

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const save = useMutation({
    mutationFn: (override: number | null) => projectsApi.setEstimatedHours(projectId, override),
    onSuccess: (data: EstimatedHours) => {
      qc.setQueryData(['estimated-hours', projectId], data);
      qc.invalidateQueries({ queryKey: ['project', projectId] });
      setEditing(false);
    },
  });

  if (eh.isLoading) {
    return (
      <div className="pw-card eh-card">
        <div className="mp-card-hd"><h2 className="mp-title">Projected hours</h2></div>
        <div className="eh-body"><span className="mp-sub">Loading projected hours…</span></div>
      </div>
    );
  }
  if (eh.isError || !eh.data) {
    return (
      <div className="pw-card eh-card">
        <div className="mp-card-hd"><h2 className="mp-title">Projected hours</h2></div>
        <div className="eh-body"><span className="mp-sub">Couldn't load projected hours.</span></div>
      </div>
    );
  }

  const d = eh.data;
  const { effective, source, derived, reference, referenceSyncedAt, enteredAt } = d;
  const onFile = source === 'estimator';
  const preloaded = source === 'derived';

  // THREE states now (product owner, 2026-08-18: "pre-load the man hours ... and still
  // give the option for the estimator to override it"):
  //   typed     — someone entered a figure; it wins outright.
  //   derived   — pre-loaded from the labor estimate ÷ the company $/MH rate.
  //               NOT the old Intacct autocalc coming back: that number was
  //               Intacct's own calculation and ran low (product owner, 2026-08-04,
  //               still demoted to `reference` below). This one is the
  //               estimators' pricing math run backwards.
  //   absent    — nothing typed AND no labor budget synced; flagged.
  // ⚠️ Name the person who actually typed it, not a role. This said "Entered by
  // the estimator", which the app never verified — anyone with projects.edit can
  // enter hours (product owner, 2026-08-04: "I was the person that entered the hours not
  // the estimator"). Falls back to the neutral wording when the name is absent.
  const enteredWhen = enteredAt ? ` · ${fmtDate(enteredAt)}` : '';
  const sourceLabel = onFile
    ? (d.enteredByName ? `Entered by ${d.enteredByName}${enteredWhen}` : `Entered manually${enteredWhen}`)
    : preloaded
      ? `Pre-loaded from the labor estimate at $${derived!.rate}/MH`
      : 'Not entered yet';

  function beginEdit() {
    setDraft(effective != null ? String(effective) : '');
    save.reset();
    setEditing(true);
  }
  const parsed = draft.trim() === '' ? null : Number(draft);
  const invalid = parsed != null && (!Number.isFinite(parsed) || parsed < 0);

  // Actual vs projected: only meaningful with a positive projected target.
  const showCompare = actualHours != null && effective != null && effective > 0;
  const pct = showCompare ? (actualHours! / effective!) * 100 : 0;
  const over = showCompare && actualHours! > effective!;
  const barColor = over ? '#b91c1c' : pct > 85 ? '#E0A100' : 'var(--ew-green, #2d8c54)';

  return (
    <div className="pw-card eh-card">
      <div className="mp-card-hd">
        <h2 className="mp-title">Projected hours</h2>
        <span className="mp-sub">
          {sourceLabel}
          {/* The flag. Amber, and only when this job is actually expected to have
              a figure — a Complete or SYSTAC job is exempt (lib/laborHours). */}
          {d.state === 'missing' && (
            <span className="eh-badge" style={{ marginLeft: 8, color: '#92400e', background: '#fef3c7' }}>
              Needs hours
            </span>
          )}
        </span>
      </div>

      <div className="eh-body">
        <div className="eh-figure">
          <span className="eh-value">{effective != null ? fmtHrs(effective) : '—'}</span>
          <span className="eh-unit">hrs projected</span>
        </div>

        <div className="eh-meta">
          {/* ⚠️ The old Intacct figure is shown as a REFERENCE only, never as the
              value, and is explicitly labelled unverified and dated. It is frozen:
              nothing refreshes it since the weekly sync was removed, and it was
              autocalculated low, which is why it is not the estimate.

              ⚠️ TWO WORDINGS. Once a figure is on file, "Enter the real figure"
              is nonsense — the real figure is already there and sits two inches to
              the left (product owner, 2026-08-04). With hours entered this becomes a plain
              comparison; without them it keeps the instruction. */}
          {/* When an override is on file, show the derived figure it replaced —
              the same cross-check the Man Hours report shows, and how a PM
              notices a typo'd override (job 10952: 776 typed vs 24,584 derived). */}
          {onFile && derived != null ? (
            <div className="eh-breakdown" style={{ color: 'var(--ew-n-500)' }}>
              The labor estimate derives <b>{fmtHrs(derived.hours)} hrs</b> at ${derived.rate}/MH —
              the typed figure above overrides it.
            </div>
          ) : null}
          {preloaded ? (
            <div className="eh-breakdown" style={{ color: 'var(--ew-n-500)' }}>
              Derived from the job&apos;s estimated labor dollars ÷ ${derived!.rate}/MH (the estimators&apos;
              pricing rate, set in Admin ▸ Config). Override it below if the real figure differs.
              {/* Pending scope is IN the figure (so it ties to the reports, which
                  keep it in and disclose it) — and a target that includes
                  unapproved work is exactly what a PM needs told. */}
              {derived!.pendingChangeHours > 0 ? (
                <> Includes <b>{fmtHrs(derived!.pendingChangeHours)} hrs</b> from pending (not-yet-approved) change scope.</>
              ) : null}
            </div>
          ) : null}
          {/* Switched-off sync with historical rows is the sneaky state: the
              figure still LOOKS live. Same warning the Man Hours report shows,
              because this card is a separate surface for the same number. */}
          {preloaded && !derived!.estimateEnabled ? (
            <div className="eh-breakdown" style={{ color: '#8A4E12' }}>
              ⚠ The Intacct estimate sync is switched off — this figure reflects the last synced
              estimate{derived!.estimateSyncedAt ? ` (${fmtDate(derived!.estimateSyncedAt)})` : ''}, not the
              current one.
            </div>
          ) : null}
          {reference != null ? (
            <div className="eh-breakdown" style={{ color: 'var(--ew-n-500)' }}>
              {onFile || preloaded ? (
                <>
                  Intacct had calculated <b>{fmtHrs(reference)} hrs</b>
                  {referenceSyncedAt ? ` (${fmtDate(referenceSyncedAt)})` : ''} — autocalculated,
                  and kept only for comparison.
                </>
              ) : (
                <>
                  For reference, Intacct calculated <b>{fmtHrs(reference)} hrs</b>
                  {referenceSyncedAt ? ` (${fmtDate(referenceSyncedAt)})` : ''} — unverified, and usually
                  low. Enter the real figure.
                </>
              )}
            </div>
          ) : !onFile && !preloaded ? (
            <div className="eh-breakdown">
              No estimated hours on file, and no labor estimate has synced to derive them from —
              the estimator enters these at job set-up.
            </div>
          ) : null}

          {showCompare && (
            <>
              <div className="eh-prog-track" title={`${fmtHrs(actualHours!)} actual / ${fmtHrs(effective!)} projected`}>
                <div className="eh-prog-fill" style={{ width: `${Math.min(100, pct)}%`, background: barColor }} />
              </div>
              <div className="eh-prog-label">
                <b>{fmtHrs(actualHours!)}</b> actual of <b>{fmtHrs(effective!)}</b> projected · {pct.toFixed(0)}%
                {over
                  ? <span style={{ color: '#b91c1c' }}> · over by {fmtHrs(actualHours! - effective!)} hrs</span>
                  : <span className="mp-muted"> · {fmtHrs(effective! - actualHours!)} hrs remaining</span>}
              </div>
            </>
          )}

          {canEdit && (
            editing ? (
              <div className="eh-actions">
                <input
                  className="eh-input"
                  type="number"
                  min={0}
                  step="any"
                  inputMode="decimal"
                  autoFocus
                  value={draft}
                  placeholder="hours"
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !invalid) save.mutate(parsed); if (e.key === 'Escape') setEditing(false); }}
                />
                <button className="eh-btn eh-btn-primary" disabled={invalid || save.isPending} onClick={() => save.mutate(parsed)}>
                  {save.isPending ? 'Saving…' : 'Save'}
                </button>
                <button className="eh-btn" disabled={save.isPending} onClick={() => setEditing(false)}>Cancel</button>
                <span className="mp-sub">
                  {derived != null
                    ? `Clearing the box and saving returns to the derived ${fmtHrs(derived.hours)} hrs.`
                    : 'Clearing the box and saving takes the hours back off the job.'}
                </span>
                {save.isError && <span className="eh-err">Couldn't save — try again.</span>}
              </div>
            ) : (
              <div className="eh-actions">
                {/* "Reset to Intacct" is gone with the baseline it reverted to.
                    A pre-loaded figure gets "Override" — editing it converts the
                    derived number into a typed one that stops tracking the
                    estimate, and the verb should say so. */}
                <button className="eh-btn" onClick={beginEdit}>
                  {onFile ? 'Edit' : preloaded ? 'Override' : 'Enter hours'}
                </button>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Actual daily manpower from Paycor (read-only) ───────────────────────────

function PaycorSection({ q, actualStart }: { q: UseQueryResult<ManpowerActuals>; actualStart: string | null }) {
  /**
   * The Actual start tile survives every branch in which the hours feed has
   * nothing to show, because it is not a Paycor fact (#482).
   *
   * A phase's recorded actual start stands on its own — fabrication's is typed
   * into the schedule drawer by hand, and a job that began today has a date
   * before the nightly feed has a single row against it. Gating the tile on the
   * feed would make those dates invisible now the Overview card no longer prints
   * one, which is the regression this PR is otherwise removing.
   *
   * Only this one tile: the other five ARE Paycor facts and have nothing to say
   * here. "Last day worked" in particular is absent, so the pair no longer
   * bookends anything and the lone tile is the whole row.
   */
  const startOnlyTiles = actualStart ? (
    <div className="mp-tiles">
      <div className="mp-kpi">
        <span className="mp-kpi-label">Actual start</span>
        <span className="mp-kpi-value">{fmtDate(actualStart)}</span>
      </div>
    </div>
  ) : null;

  if (q.isLoading) return <ComingSoon title="Daily manpower" note="Loading daily manpower from Paycor…" />;
  if (q.isError || !q.data?.available) {
    return (
      <>
        {startOnlyTiles}
        <ComingSoon title="Daily manpower" note="Couldn't reach the Paycor hours feed (check db_datareader on safety-site)." />
      </>
    );
  }
  const { summary, daily } = q.data;
  if (!summary || daily.length === 0) {
    return (
      <>
        {startOnlyTiles}
        <ComingSoon title="Daily manpower" note="No Paycor hours have been logged against this job yet." />
      </>
    );
  }
  const showDt = daily.some((d) => d.dtHours > 0);
  const maxHours = daily.reduce((m, d) => Math.max(m, d.hours), 0) || 1;
  const tiles = [
    { label: 'Total field hours', value: fmtHrs(summary.totalHours) },
    { label: 'Workers', value: String(summary.workers) },
    { label: 'Avg crew · last 10 days', value: String(summary.avgRecentCrew) },
    { label: 'Peak crew', value: String(summary.peakCrew) },
    // Before Last day worked on purpose: the two bookend the field work, and a
    // reader scanning the row gets the span rather than two loose dates (#482).
    { label: 'Actual start', value: fmtDate(actualStart) },
    { label: 'Last day worked', value: fmtDate(summary.lastWorkDate) },
  ];

  return (
    <>
      <div className="mp-tiles">
        {tiles.map((t) => (
          <div key={t.label} className="mp-kpi">
            <span className="mp-kpi-label">{t.label}</span>
            <span className="mp-kpi-value">{t.value}</span>
          </div>
        ))}
      </div>

      <div className="pw-card">
        <div className="mp-card-hd">
          <h2 className="mp-title">Daily manpower</h2>
          <span className="mp-sub">
            Actual Paycor hours · {summary.daysWorked} days worked ·
            <span className="mp-legend-reg" />Reg {fmtHrs(summary.regHours)}
            <span className="mp-legend-ot" />OT {fmtHrs(summary.otHours)}{showDt ? ` · DT ${fmtHrs(summary.dtHours)}` : ''}
          </span>
        </div>
        <div className="mp-scroll">
          <table className="mp-table">
            <thead>
              <tr>
                <th>Date</th>
                <th className="mp-num">Crew</th>
                <th className="mp-num">Total hrs</th>
                <th className="mp-num">Reg</th>
                <th className="mp-num">OT</th>
                {showDt ? <th className="mp-num">DT</th> : null}
                <th className="mp-bar-col" aria-label="Reg / OT by day" />
              </tr>
            </thead>
            <tbody>
              {daily.map((d) => (
                <tr key={d.workDate}>
                  <td>{fmtDate(d.workDate)}</td>
                  <td className="mp-num">{d.crew}</td>
                  <td className="mp-num">{fmtHrs(d.hours)}</td>
                  <td className="mp-num mp-muted">{fmtHrs(d.regHours)}</td>
                  <td className="mp-num" style={{ color: d.otHours > 0 ? '#92400E' : 'var(--ew-n-500)' }}>
                    {d.otHours > 0 ? fmtHrs(d.otHours) : '—'}
                  </td>
                  {showDt ? <td className="mp-num">{d.dtHours > 0 ? fmtHrs(d.dtHours) : '—'}</td> : null}
                  <td className="mp-bar-col">
                    <div className="mp-bar-track" title={`${fmtHrs(d.hours)} hrs`}>
                      <span className="mp-bar-reg" style={{ width: `${(d.regHours / maxHours) * 100}%` }} />
                      <span className="mp-bar-ot" style={{ width: `${((d.otHours + d.dtHours) / maxHours) * 100}%` }} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
